import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import QimenChart from "@/components/QimenChart";
import ChartMetadata from "@/components/ChartMetadata";
import AnalysisPanel from "@/components/AnalysisPanel";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, ChevronLeft, ChevronRight, Calendar, Clock, X, Info, ChevronDown, ChevronUp, BarChart3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palace positions in the 3x3 grid (row, col)
const PALACE_POSITIONS = [
  { id: "SE", name: "Southeast", row: 0, col: 0 },
  { id: "S", name: "South", row: 0, col: 1 },
  { id: "SW", name: "Southwest", row: 0, col: 2 },
  { id: "E", name: "East", row: 1, col: 0 },
  { id: "CENTER", name: "Center", row: 1, col: 1 },
  { id: "W", name: "West", row: 1, col: 2 },
  { id: "NE", name: "Northeast", row: 2, col: 0 },
  { id: "N", name: "North", row: 2, col: 1 },
  { id: "NW", name: "Northwest", row: 2, col: 2 },
];

// Two-hour periods in Qi Men (for reference)
const TWO_HOUR_PERIODS = [
  { hour: 0, label: "00:00 - 02:00", period: "子時" },
  { hour: 2, label: "02:00 - 04:00", period: "丑時" },
  { hour: 4, label: "04:00 - 06:00", period: "寅時" },
  { hour: 6, label: "06:00 - 08:00", period: "卯時" },
  { hour: 8, label: "08:00 - 10:00", period: "辰時" },
  { hour: 10, label: "10:00 - 12:00", period: "巳時" },
  { hour: 12, label: "12:00 - 14:00", period: "午時" },
  { hour: 14, label: "14:00 - 16:00", period: "未時" },
  { hour: 16, label: "16:00 - 18:00", period: "申時" },
  { hour: 18, label: "18:00 - 20:00", period: "酉時" },
  { hour: 20, label: "20:00 - 22:00", period: "戌時" },
  { hour: 22, label: "22:00 - 00:00", period: "亥時" },
];

// All 24 hours for selection
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00`,
  range: `${String(i).padStart(2, '0')}:00 - ${String(i).padStart(2, '0')}:59`,
}));

// Get Chinese period for an hour
const getChinesePeriod = (hour) => {
  const periodIndex = Math.floor(hour / 2);
  return TWO_HOUR_PERIODS[periodIndex]?.period || "";
};

function TimeNavigator({ year, month, day, hour, onTimeChange, isGuest }) {
  const currentHour = parseInt(hour) || 0;
  
  // Generate days for the current month
  const daysInMonth = new Date(year, month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  
  // Generate months
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handlePrevPeriod = () => {
    let newHour, newDay = day, newMonth = month, newYear = year;
    
    if (currentHour === 0) {
      // Go to previous day, last hour (23)
      newHour = 23;
      newDay = parseInt(day) - 1;
      if (newDay < 1) {
        newMonth = parseInt(month) - 1;
        if (newMonth < 1) {
          newMonth = 12;
          newYear = parseInt(year) - 1;
        }
        newDay = new Date(newYear, newMonth, 0).getDate();
      }
    } else {
      newHour = currentHour - 1;
    }
    
    onTimeChange(newYear, newMonth, newDay, newHour);
  };

  const handleNextPeriod = () => {
    let newHour, newDay = day, newMonth = month, newYear = year;
    
    if (currentHour === 23) {
      // Go to next day, first hour (0)
      newHour = 0;
      newDay = parseInt(day) + 1;
      const maxDay = new Date(year, month, 0).getDate();
      if (newDay > maxDay) {
        newDay = 1;
        newMonth = parseInt(month) + 1;
        if (newMonth > 12) {
          newMonth = 1;
          newYear = parseInt(year) + 1;
        }
      }
    } else {
      newHour = currentHour + 1;
    }
    
    onTimeChange(newYear, newMonth, newDay, newHour);
  };

  const chinesePeriod = getChinesePeriod(currentHour);

  return (
    <div className="time-navigator" data-testid="time-navigator">
      <div className="time-nav-row">
        {/* Date Selectors */}
        <div className="date-selectors">
          <div className="date-selector-group">
            <Calendar size={14} className="selector-icon" />
            <Select 
              value={String(year)} 
              onValueChange={(v) => onTimeChange(parseInt(v), month, day, currentHour)}
              disabled={isGuest}
            >
              <SelectTrigger className="year-select" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={String(month)} 
              onValueChange={(v) => onTimeChange(year, parseInt(v), day, currentHour)}
              disabled={isGuest}
            >
              <SelectTrigger className="month-select" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={String(day)} 
              onValueChange={(v) => onTimeChange(year, month, parseInt(v), currentHour)}
              disabled={isGuest}
            >
              <SelectTrigger className="day-select" data-testid="day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {days.map(d => (
                  <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Time Period Navigator */}
        <div className="period-navigator">
          <Button
            variant="ghost"
            size="sm"
            className="nav-arrow"
            onClick={handlePrevPeriod}
            disabled={isGuest}
            data-testid="prev-period-btn"
          >
            <ChevronLeft size={20} />
          </Button>
          
          <div className="period-selector-group">
            <Clock size={14} className="selector-icon" />
            <Select 
              value={String(currentHour)} 
              onValueChange={(v) => onTimeChange(year, month, day, parseInt(v))}
              disabled={isGuest}
            >
              <SelectTrigger className="period-select" data-testid="period-select">
                <SelectValue>
                  {String(currentHour).padStart(2, '0')}:00 - {String(currentHour).padStart(2, '0')}:59 {chinesePeriod}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ALL_HOURS.map(h => (
                  <SelectItem key={h.value} value={String(h.value)}>
                    {h.range} {getChinesePeriod(h.value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            className="nav-arrow"
            onClick={handleNextPeriod}
            disabled={isGuest}
            data-testid="next-period-btn"
          >
            <ChevronRight size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartImages, setChartImages] = useState({});
  const [zoomedPalace, setZoomedPalace] = useState(null);
  const [lastZoomedPalace, setLastZoomedPalace] = useState(null);
  const [infoImage, setInfoImage] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [sourceImage, setSourceImage] = useState(null);
  const [availableCategories, setAvailableCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategories, setShowCategories] = useState(false);

  // Check if user is admin
  const isAdmin = !!localStorage.getItem("adminToken");
  const isGuest = !isAdmin;

  // Guest restriction: redirect to today's date + current hour
  useEffect(() => {
    if (!isGuest) return;
    const now = new Date();
    const todayYear = String(now.getFullYear());
    const todayMonth = String(now.getMonth() + 1);
    const todayDay = String(now.getDate());
    const todayHour = String(now.getHours());

    const paramYear = searchParams.get("year");
    const paramMonth = searchParams.get("month");
    const paramDay = searchParams.get("day");
    const paramHour = searchParams.get("hour") || "0";

    if (paramYear !== todayYear || paramMonth !== todayMonth || paramDay !== todayDay || paramHour !== todayHour) {
      const params = new URLSearchParams({
        year: todayYear,
        month: todayMonth,
        day: todayDay,
        hour: todayHour,
        minute: "0",
        system: searchParams.get("system") || "selectZhiRun",
      });
      navigate(`/result?${params.toString()}`, { replace: true });
    }
  }, [isGuest, searchParams, navigate]);

  const CATEGORIES = [
    { value: "business", label: "Business", color: "#3b82f6" },
    { value: "wealth", label: "Wealth", color: "#f59e0b" },
    { value: "career", label: "Career", color: "#8b5cf6" },
    { value: "health", label: "Health", color: "#10b981" },
    { value: "academics", label: "Academics", color: "#06b6d4" },
    { value: "relationship", label: "Relationship", color: "#ec4899" },
    { value: "recognition", label: "Recognition", color: "#f97316" },
  ];

  // Handle zoom with animation
  const handleZoomIn = (palace) => {
    setLastZoomedPalace(palace);
    setZoomedPalace(palace);
  };

  const handleZoomOut = () => {
    setZoomedPalace(null);
    // Keep lastZoomedPalace for smooth animation, clear after transition
    setTimeout(() => setLastZoomedPalace(null), 400);
  };

  // Fetch chart images list for navigation
  useEffect(() => {
    const fetchChartImages = async () => {
      try {
        const res = await axios.get(`${API}/qimen/lookup/status`);
        setChartImages(res.data.chart_images || {});
      } catch (err) {
        console.error("Failed to fetch chart images:", err);
      }
    };
    fetchChartImages();
  }, []);

  // Fetch source image based on date/hour (instead of URL param)
  useEffect(() => {
    const year = searchParams.get("year");
    const month = searchParams.get("month");
    const day = searchParams.get("day");
    const hour = searchParams.get("hour") || "0";
    
    if (!year || !month || !day) return;
    
    // Build the date key to look up the image (ensure proper padding)
    const dateKey = `${year}_${String(month).padStart(2, '0')}_${String(day).padStart(2, '0')}_${String(hour).padStart(2, '0')}`;
    
    console.log("Looking for chart image with key:", dateKey, "Available:", Object.keys(chartImages).length);
    
    // Check if image exists in chartImages
    if (chartImages[dateKey]) {
      console.log("Found image:", chartImages[dateKey]);
      setSourceImage(`${process.env.REACT_APP_BACKEND_URL}${chartImages[dateKey]}`);
    } else {
      setSourceImage(null);
    }
  }, [searchParams, chartImages]);

  const fetchChart = useCallback(async () => {
    const year = searchParams.get("year");
    const month = searchParams.get("month");
    const day = searchParams.get("day");
    const hour = searchParams.get("hour") || "0";
    const minute = searchParams.get("minute") || "0";
    const system = searchParams.get("system") || "selectZhiRun";

    if (!year || !month || !day) {
      setError("Missing date parameters. Please go back and select a date.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const debug = searchParams.get("debug") === "true";
      const res = await axios.get(`${API}/qimen/chart`, {
        params: { year, month, day, hour, minute, system, debug },
      });
      
      // Check for error response (chart not found)
      if (res.data.error) {
        setError(res.data.error);
        return;
      }
      
      setChartData(res.data.chart);
      setAnalysisData(res.data.analysis);
    } catch (err) {
      console.error("Chart fetch error:", err);
      setError("Failed to generate chart. Chart may not exist in canonical lookup table.");
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  // Fetch info image for the current date+hour
  useEffect(() => {
    const fetchInfoImage = async () => {
      const year = searchParams.get("year");
      const month = searchParams.get("month");
      const day = searchParams.get("day");
      const hour = parseInt(searchParams.get("hour") || "0");
      
      if (!year || !month || !day) return;
      
      // Convert storage hour to display hour (0 -> 24, 1-23 -> 1-23)
      const displayHour = hour === 0 ? 24 : hour;
      
      try {
        const res = await axios.get(`${API}/info/image`, {
          params: { year, month, day, hour: displayHour }
        });
        if (res.data.success && res.data.url) {
          setInfoImage(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
        } else {
          setInfoImage(null);
        }
      } catch (err) {
        console.error("Failed to fetch info image:", err);
        setInfoImage(null);
      }
    };
    fetchInfoImage();
  }, [searchParams]);

  // Fetch available category images for the current date+hour
  useEffect(() => {
    const fetchCategories = async () => {
      const year = searchParams.get("year");
      const month = searchParams.get("month");
      const day = searchParams.get("day");
      const hour = parseInt(searchParams.get("hour") || "0");
      if (!year || !month || !day) return;
      const displayHour = hour === 0 ? 24 : hour;
      try {
        const res = await axios.get(`${API}/category/available`, {
          params: { year, month, day, hour: displayHour }
        });
        if (res.data.success) {
          setAvailableCategories(res.data.available || {});
        } else {
          setAvailableCategories({});
        }
      } catch {
        setAvailableCategories({});
      }
      setSelectedCategory(null);
    };
    fetchCategories();
  }, [searchParams]);

  const handleTimeChange = (newYear, newMonth, newDay, newHour) => {
    const system = searchParams.get("system") || "selectZhiRun";
    
    // Build the date key for the new time
    const dateKey = `${newYear}_${String(newMonth).padStart(2, '0')}_${String(newDay).padStart(2, '0')}_${String(newHour).padStart(2, '0')}`;
    
    // Check if the new time has an uploaded image
    const params = new URLSearchParams({
      year: String(newYear),
      month: String(newMonth),
      day: String(newDay),
      hour: String(newHour),
      minute: "0",
      system,
    });
    
    // Don't include sourceImage in URL - it will be fetched automatically based on date/hour
    navigate(`/result?${params.toString()}`);
  };

  // Get current params for TimeNavigator
  const currentYear = parseInt(searchParams.get("year")) || new Date().getFullYear();
  const currentMonth = parseInt(searchParams.get("month")) || new Date().getMonth() + 1;
  const currentDay = parseInt(searchParams.get("day")) || new Date().getDate();
  const currentHour = parseInt(searchParams.get("hour")) || 0;
  // sourceImage is now fetched from state based on date/hour, not from URL

  if (loading) {
    return (
      <div className="result-page" data-testid="result-page-loading">
        <div className="loading-container">
          <Loader2 className="animate-spin" size={48} />
          <p>Generating Qi Men Chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="result-page" data-testid="result-page-error">
        <div className="error-container">
          <p>{error}</p>
          <Button onClick={() => navigate("/")} data-testid="back-home-btn">
            <ArrowLeft size={16} /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="result-page" data-testid="result-page">
      {/* Top Bar */}
      <div className="result-topbar">
        <Button
          variant="ghost"
          className="back-btn"
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
          Back
        </Button>
        <div className="topbar-title" data-testid="topbar-title">
          Qi Men Dun Jia Chart
        </div>
        <div className="topbar-actions">
        </div>
      </div>

      {/* Metadata - Hide when viewing uploaded image */}
      {chartData && !sourceImage && <ChartMetadata metadata={chartData.metadata} input={chartData.datetime} debug={chartData.debug} />}

      {/* Time Navigator - Always show for navigation */}
      <TimeNavigator
        year={currentYear}
        month={currentMonth}
        day={currentDay}
        hour={currentHour}
        onTimeChange={handleTimeChange}
        isGuest={isGuest}
      />

      {/* Main Content */}
      <div className={`result-content ${sourceImage ? "with-source-image" : ""}`}>
        {/* Source Image - Shows when sourceImage is provided (no label) */}
        {sourceImage && (
          <div className="source-image-panel no-header full-width" data-testid="source-image-panel">
            <div 
              className={`source-image-container ${zoomedPalace ? "zoomed" : ""}`}
              onClick={() => zoomedPalace && handleZoomOut()}
            >
              <div 
                className="source-image-wrapper"
                style={{
                  transform: zoomedPalace ? `scale(1.5)` : 'scale(1)',
                  transformOrigin: (zoomedPalace || lastZoomedPalace) 
                    ? `${((zoomedPalace || lastZoomedPalace).col * 50)}% ${((zoomedPalace || lastZoomedPalace).row * 50)}%`
                    : 'center center',
                }}
              >
                <img 
                  src={sourceImage} 
                  alt="Chart" 
                  className="source-image"
                  style={{ borderRadius: 0 }}
                  data-testid="source-image"
                />
                {/* Center palace logo overlay - covers the white box */}
                <div className="center-palace-logo-overlay">
                  <img 
                    src="/center_logo.png" 
                    alt="QMDJ Logo" 
                    className="center-palace-logo"
                  />
                </div>
                {/* Clickable palace grid overlay - only show when not zoomed */}
                {!zoomedPalace && (
                  <div className="palace-click-grid">
                    {PALACE_POSITIONS.map((palace) => (
                      <button
                        key={palace.id}
                        className="palace-click-area"
                        style={{
                          gridRow: palace.row + 1,
                          gridColumn: palace.col + 1,
                        }}
                        onClick={() => handleZoomIn(palace)}
                        data-testid={`palace-click-${palace.id}`}
                        title={`Click to zoom ${palace.name}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Zoom indicator */}
              {zoomedPalace && (
                <div className="zoom-indicator">
                  <span>Click anywhere to zoom out</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Image Toggle - displayed below chart when available */}
        {infoImage && (
          <div className="info-toggle-section" data-testid="info-toggle-section">
            <Button
              variant="outline"
              className={`info-toggle-btn ${showInfo ? 'active' : ''}`}
              onClick={() => setShowInfo(!showInfo)}
              data-testid="info-toggle-btn"
            >
              <Info size={18} />
              {showInfo ? 'Hide' : 'Show'} Important Info
              {showInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </Button>
            
            {showInfo && (
              <div className="info-image-wrapper">
                <img 
                  src={infoImage} 
                  alt="Important Info" 
                  className="info-image-display"
                  data-testid="info-image"
                />
              </div>
            )}
          </div>
        )}

        {/* Category Forecasts Section */}
        {Object.keys(availableCategories).length > 0 && (
          <div className="category-forecast-section" data-testid="category-forecast-section">
            <Button
              variant="outline"
              className={`info-toggle-btn ${showCategories ? 'active' : ''}`}
              onClick={() => { setShowCategories(!showCategories); if (showCategories) setSelectedCategory(null); }}
              data-testid="category-toggle-btn"
            >
              <BarChart3 size={18} />
              {showCategories ? 'Hide' : 'Show'} Category Forecasts
              {showCategories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </Button>

            {showCategories && (
              <div className="category-buttons-wrapper" data-testid="category-buttons">
                <div className="category-buttons-row">
                  {CATEGORIES.filter(c => availableCategories[c.value]).map(c => (
                    <button
                      key={c.value}
                      className={`category-btn ${selectedCategory === c.value ? 'active' : ''}`}
                      style={{
                        '--cat-color': c.color,
                        borderColor: selectedCategory === c.value ? c.color : 'transparent',
                        background: selectedCategory === c.value ? `${c.color}15` : undefined,
                      }}
                      onClick={() => setSelectedCategory(selectedCategory === c.value ? null : c.value)}
                      data-testid={`category-btn-${c.value}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                {selectedCategory && availableCategories[selectedCategory] && (
                  <div className="category-image-wrapper" data-testid="category-image-wrapper">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${availableCategories[selectedCategory]}`}
                      alt={`${selectedCategory} forecast`}
                      className="info-image-display"
                      data-testid="category-image"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Only show chart area when NO source image */}
        {!sourceImage && (
          <>
            <div className="chart-area">
              {chartData && (
                <QimenChart
                  palaces={chartData.palaces}
                  gridLayout={chartData.grid_layout}
                  metadata={chartData.metadata}
                />
              )}
            </div>

            <div className="analysis-area">
              <Tabs defaultValue="analysis" className="analysis-tabs">
                <TabsList className="analysis-tabs-list">
                  <TabsTrigger value="analysis" data-testid="analysis-tab">
                    Analysis
                  </TabsTrigger>
                  <TabsTrigger value="pillars" data-testid="pillars-tab">
                    Four Pillars
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="analysis">
                  {analysisData && <AnalysisPanel analysis={analysisData} />}
                </TabsContent>
                <TabsContent value="pillars">
                  {chartData && chartData.metadata.four_pillars ? (
                    <div className="pillars-panel" data-testid="pillars-panel">
                      {["year", "month", "day", "hour"].map((pillar) => {
                        const p = chartData.metadata.four_pillars[pillar];
                        return (
                          <div key={pillar} className="pillar-card">
                            <div className="pillar-label">{pillar.charAt(0).toUpperCase() + pillar.slice(1)}</div>
                            <div className="pillar-chinese">{p.label}</div>
                            <div className="pillar-detail">
                              <span className={`stem-color ${p.stem.element.toLowerCase()}`}>
                                {p.stem.chinese} {p.stem.pinyin}
                              </span>
                            </div>
                            <div className="pillar-detail">
                              <span>{p.branch.chinese} {p.branch.pinyin}</span>
                            </div>
                            <div className="pillar-element">{p.stem.yinyang} {p.stem.element}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="pillars-panel pillars-unavailable" data-testid="pillars-panel">
                      <p>Four pillars data not available for this chart (date-based lookup).</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
